// Phase 0093-B0 — HR Default Configuration Admin page.
//
// Three vertical sections:
//   1. Completeness banner (READY / WARNING / BLOCKED)
//   2. Section overview table (factory vs admin counts)
//   3. Per-section row drill-down (expand to see codes, mark-as-modified
//      and factory-reset actions)
//
// Wired at /hr/default-config.

import { useState } from 'react'
import {
  Alert, App, Badge, Button, Card, Col, Empty, Popconfirm, Row, Skeleton,
  Space, Table, Tag, Tooltip, Typography,
} from 'antd'
import {
  CheckCircleOutlined, ExclamationCircleOutlined, ReloadOutlined,
  RollbackOutlined, ToolOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { defaultConfigApi, type SectionRow, type SectionSummary } from '@/features/hr/defaultConfigApi'

const { Text, Title } = Typography

function StatusTag({ status }: { status: 'READY' | 'WARNING' | 'BLOCKED' }) {
  if (status === 'READY') {
    return <Tag color="success" icon={<CheckCircleOutlined />}>READY</Tag>
  }
  if (status === 'WARNING') {
    return <Tag color="warning" icon={<ExclamationCircleOutlined />}>WARNING</Tag>
  }
  return <Tag color="error" icon={<ExclamationCircleOutlined />}>BLOCKED</Tag>
}

export default function HRDefaultConfigPage() {
  const qc = useQueryClient()
  const { message } = App.useApp()
  const [expanded, setExpanded] = useState<string | null>(null)

  const completeness = useQuery({
    queryKey: ['hr-default-config-completeness'],
    queryFn: () => defaultConfigApi.completeness(),
  })

  const sectionRows = useQuery({
    queryKey: ['hr-default-config-rows', expanded],
    queryFn: () => defaultConfigApi.rows(expanded!),
    enabled: !!expanded,
  })

  const markMut = useMutation({
    mutationFn: ({ section, rowId }: { section: string; rowId: string }) =>
      defaultConfigApi.markModified(section, rowId),
    onSuccess: () => {
      message.success('Marked as admin-modified — seed reruns will leave it alone.')
      qc.invalidateQueries({ queryKey: ['hr-default-config-rows', expanded] })
      qc.invalidateQueries({ queryKey: ['hr-default-config-completeness'] })
    },
  })

  const resetMut = useMutation({
    mutationFn: ({ section, rowId }: { section: string; rowId: string }) =>
      defaultConfigApi.resetToFactory(section, rowId),
    onSuccess: () => {
      message.success('Row reset to factory defaults.')
      qc.invalidateQueries({ queryKey: ['hr-default-config-rows', expanded] })
      qc.invalidateQueries({ queryKey: ['hr-default-config-completeness'] })
    },
  })

  const reseedMut = useMutation({
    mutationFn: () => defaultConfigApi.reseed(),
    onSuccess: () => {
      message.success('Factory defaults reseeded.')
      qc.invalidateQueries({ queryKey: ['hr-default-config-completeness'] })
      if (expanded) {
        qc.invalidateQueries({ queryKey: ['hr-default-config-rows', expanded] })
      }
    },
  })

  if (completeness.isLoading) {
    return <Card style={{ margin: 16 }}><Skeleton active /></Card>
  }

  const report = completeness.data
  if (!report) return null

  const sectionColumns = [
    { title: 'Section', dataIndex: 'label', key: 'label',
      render: (label: string, row: SectionSummary) => (
        <Space>
          <Text strong>{label}</Text>
          {row.critical && <Tag color="red">CRITICAL</Tag>}
        </Space>
      ) },
    { title: 'Total', dataIndex: 'total', key: 'total' },
    { title: 'Factory', dataIndex: 'seeded', key: 'seeded',
      render: (n: number) => <Tag color="blue">{n}</Tag> },
    { title: 'Admin-modified', dataIndex: 'admin_modified', key: 'admin_modified',
      render: (n: number) => n > 0 ? <Tag color="gold">{n}</Tag> : '—' },
    { title: 'Active', dataIndex: 'active', key: 'active',
      render: (n: number, r: SectionSummary) => (
        <Tag color={n === 0 ? (r.critical ? 'error' : 'default')
                    : r.active === r.total ? 'success' : 'warning'}>
          {n} / {r.total}
        </Tag>
      ) },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: '', key: 'action',
      render: (_: unknown, r: SectionSummary) => (
        <Button size="small"
                onClick={() => setExpanded(
                  expanded === r.section_code ? null : r.section_code)}>
          {expanded === r.section_code ? 'Hide' : 'View rows'}
        </Button>
      ) },
  ]

  const rowColumns = [
    { title: 'Code', dataIndex: 'code', key: 'code',
      render: (c: string | null) => c ? <code>{c}</code> : '—' },
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Active', dataIndex: 'active', key: 'active',
      render: (a: boolean) => <Badge status={a ? 'success' : 'default'}
                                      text={a ? 'Active' : 'Inactive'} /> },
    { title: 'Source', key: 'source',
      render: (_: unknown, r: SectionRow) => {
        if (r.admin_modified_at) {
          return (
            <Tooltip title={`Edited at ${r.admin_modified_at}`}>
              <Tag color="gold">Admin-modified</Tag>
            </Tooltip>
          )
        }
        if (r.system_seeded) return <Tag color="blue">Factory v{r.factory_version}</Tag>
        return <Tag>User-created</Tag>
      } },
    { title: 'Last seeded', dataIndex: 'last_seeded_at', key: 'last_seeded_at',
      render: (s: string | null) => s ? new Date(s).toLocaleString() : '—' },
    { title: 'Actions', key: 'actions',
      render: (_: unknown, r: SectionRow) => (
        <Space>
          {r.system_seeded && !r.admin_modified_at && (
            <Tooltip title="Mark this row as admin-edited — seed re-runs will leave it alone.">
              <Button size="small" icon={<ToolOutlined />}
                      onClick={() => markMut.mutate({
                        section: expanded!, rowId: r.id,
                      })}
                      loading={markMut.isPending}>
                Lock
              </Button>
            </Tooltip>
          )}
          {r.admin_modified_at && (
            <Popconfirm
              title="Reset to factory defaults?"
              description="The admin lock will be released and the factory payload re-applied."
              onConfirm={() => resetMut.mutate({
                section: expanded!, rowId: r.id,
              })}
            >
              <Button size="small" danger icon={<RollbackOutlined />}
                      loading={resetMut.isPending}>
                Reset
              </Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>
        HR Default Configuration{' '}
        <StatusTag status={report.overall_status} />
      </Title>
      <Text type="secondary">
        Factory-seeded HR defaults. Admin edits are preserved across seed re-runs.
      </Text>

      <div style={{ marginTop: 12 }}>
        <Button icon={<ReloadOutlined />}
                onClick={() => reseedMut.mutate()}
                loading={reseedMut.isPending}>
          Re-run factory seed
        </Button>
      </div>

      {report.issues.length > 0 && (
        <Card style={{ marginTop: 16 }} title="Issues">
          {report.issues.map((iss, idx) => (
            <Alert
              key={idx}
              type={iss.level === 'BLOCKED' ? 'error' : 'warning'}
              showIcon
              message={`${iss.section.toUpperCase()}: ${iss.message}`}
              style={{ marginBottom: 6 }}
            />
          ))}
        </Card>
      )}

      <Card style={{ marginTop: 16 }} title="Sections">
        <Table
          rowKey="section_code"
          columns={sectionColumns as never}
          dataSource={report.sections}
          pagination={false}
          size="small"
        />
      </Card>

      {expanded && (
        <Card
          style={{ marginTop: 16 }}
          title={`Rows: ${expanded}`}
          extra={
            <Button size="small" onClick={() => setExpanded(null)}>Close</Button>
          }
        >
          {sectionRows.isLoading ? (
            <Skeleton active />
          ) : sectionRows.data && sectionRows.data.length > 0 ? (
            <Table
              rowKey="id"
              columns={rowColumns as never}
              dataSource={sectionRows.data}
              pagination={{ pageSize: 20 }}
              size="small"
            />
          ) : (
            <Empty description="No rows yet — try the Re-run factory seed button." />
          )}
        </Card>
      )}
    </div>
  )
}
